import type { TranslationMessages } from "ra-core";

/**
 * Dutch translation of every key in `ra-language-english` (ra-core 5.14).
 * Informal tone ("je"); interpolations (%{name}, %{smart_count}) and the
 * " |||| " pluralisation separator are kept exactly as in the English source.
 */
export const raDutchMessages: TranslationMessages = {
  ra: {
    action: {
      add_filter: "Filter toevoegen",
      add: "Toevoegen",
      back: "Terug",
      bulk_actions:
        "1 element geselecteerd |||| %{smart_count} elementen geselecteerd",
      cancel: "Annuleren",
      clear_search: "Zoekopdracht wissen",
      clear_array_input: "Lijst leegmaken",
      clear_input_value: "Waarde wissen",
      clone: "Dupliceren",
      confirm: "Bevestigen",
      create: "Aanmaken",
      create_item: "%{item} aanmaken",
      delete: "Verwijderen",
      edit: "Bewerken",
      export: "Exporteren",
      list: "Lijst",
      refresh: "Vernieuwen",
      remove_filter: "Dit filter verwijderen",
      remove_all_filters: "Alle filters verwijderen",
      remove: "Verwijderen",
      reset: "Herstellen",
      save: "Opslaan",
      search: "Zoeken",
      search_columns: "Kolommen zoeken",
      select_all: "Alles selecteren",
      select_all_button: "Alles selecteren",
      select_row: "Deze rij selecteren",
      show: "Bekijken",
      sort: "Sorteren",
      undo: "Ongedaan maken",
      unselect: "Deselecteren",
      expand: "Uitklappen",
      close: "Sluiten",
      open_menu: "Menu openen",
      close_menu: "Menu sluiten",
      update: "Bijwerken",
      move_up: "Omhoog",
      move_down: "Omlaag",
      open: "Openen",
      toggle_theme: "Licht/donker thema wisselen",
      select_columns: "Kolommen",
      update_application: "Applicatie herladen",
    },
    boolean: {
      true: "Ja",
      false: "Nee",
      null: " ",
    },
    page: {
      create: "%{name} aanmaken",
      dashboard: "Dashboard",
      edit: "%{name} %{recordRepresentation}",
      error: "Er is iets misgegaan",
      list: "%{name}",
      loading: "Laden",
      not_found: "Niet gevonden",
      show: "%{name} %{recordRepresentation}",
      empty: "Nog geen %{name}.",
      invite: "Wil je er een toevoegen?",
      access_denied: "Geen toegang",
      authentication_error: "Authenticatiefout",
    },
    input: {
      file: {
        upload_several:
          "Sleep bestanden hierheen om te uploaden, of klik om er een te kiezen.",
        upload_single:
          "Sleep een bestand hierheen om te uploaden, of klik om het te kiezen.",
      },
      image: {
        upload_several:
          "Sleep afbeeldingen hierheen om te uploaden, of klik om er een te kiezen.",
        upload_single:
          "Sleep een afbeelding hierheen om te uploaden, of klik om deze te kiezen.",
      },
      references: {
        all_missing: "De gekoppelde gegevens zijn niet gevonden.",
        many_missing:
          "Minstens een van de gekoppelde gegevens lijkt niet meer beschikbaar te zijn.",
        single_missing:
          "Het gekoppelde gegeven lijkt niet meer beschikbaar te zijn.",
      },
      password: {
        toggle_visible: "Wachtwoord verbergen",
        toggle_hidden: "Wachtwoord tonen",
      },
    },
    message: {
      about: "Over",
      access_denied: "Je hebt geen rechten om deze pagina te bekijken",
      are_you_sure: "Weet je het zeker?",
      authentication_error:
        "De authenticatieserver gaf een fout en je gegevens konden niet worden gecontroleerd.",
      auth_error:
        "Er ging iets mis bij het controleren van het authenticatietoken.",
      bulk_delete_content:
        "Weet je zeker dat je deze %{name} wilt verwijderen? |||| Weet je zeker dat je deze %{smart_count} elementen wilt verwijderen?",
      bulk_delete_title:
        "%{name} verwijderen |||| %{smart_count} %{name} verwijderen",
      bulk_update_content:
        "Weet je zeker dat je %{name} %{recordRepresentation} wilt bijwerken? |||| Weet je zeker dat je deze %{smart_count} elementen wilt bijwerken?",
      bulk_update_title:
        "%{name} %{recordRepresentation} bijwerken |||| %{smart_count} %{name} bijwerken",
      clear_array_input: "Weet je zeker dat je de hele lijst wilt leegmaken?",
      delete_content: "Weet je zeker dat je deze %{name} wilt verwijderen?",
      delete_title: "%{name} %{recordRepresentation} verwijderen",
      details: "Details",
      error:
        "Er is een fout opgetreden en je verzoek kon niet worden uitgevoerd.",
      invalid_form: "Het formulier is niet geldig. Controleer de fouten",
      loading: "Even geduld",
      no: "Nee",
      not_found:
        "Je hebt een verkeerde URL ingetypt of een ongeldige link gevolgd.",
      select_all_limit_reached:
        "Er zijn te veel elementen om ze allemaal te selecteren. Alleen de eerste %{max} elementen zijn geselecteerd.",
      unsaved_changes:
        "Sommige wijzigingen zijn niet opgeslagen. Weet je zeker dat je ze wilt negeren?",
      yes: "Ja",
      placeholder_data_warning: "Netwerkprobleem: gegevens vernieuwen mislukt.",
    },
    navigation: {
      breadcrumb_drawer_title: "Ga naar",
      breadcrumb_drawer_instructions: "Kies een pagina om naartoe te gaan.",
      clear_filters: "Filters wissen",
      no_filtered_results: "Geen %{name} gevonden met de huidige filters.",
      no_results: "Geen %{name} gevonden",
      no_more_results: "Pagina %{page} bestaat niet. Probeer de vorige pagina.",
      page_out_of_boundaries: "Pagina %{page} bestaat niet",
      page_out_from_end: "Je kunt niet voorbij de laatste pagina",
      page_out_from_begin: "Je kunt niet voor pagina 1",
      page_range_info: "%{offsetBegin}-%{offsetEnd} van %{total}",
      partial_page_range_info:
        "%{offsetBegin}-%{offsetEnd} van meer dan %{offsetEnd}",
      current_page: "Pagina %{page}",
      page: "Ga naar pagina %{page}",
      first: "Ga naar de eerste pagina",
      last: "Ga naar de laatste pagina",
      next: "Ga naar de volgende pagina",
      previous: "Ga naar de vorige pagina",
      page_rows_per_page: "Rijen per pagina:",
      skip_nav: "Ga naar de inhoud",
    },
    sort: {
      sort_by: "Sorteren op %{field_lower_first} %{order}",
      ASC: "oplopend",
      DESC: "aflopend",
    },
    auth: {
      auth_check_error: "Log in om verder te gaan",
      user_menu: "Profiel",
      username: "Gebruikersnaam",
      password: "Wachtwoord",
      email: "E-mailadres",
      sign_in: "Inloggen",
      sign_in_error: "Inloggen mislukt, probeer het opnieuw",
      logout: "Uitloggen",
    },
    notification: {
      updated:
        "Wijzigingen opgeslagen |||| %{smart_count} elementen bijgewerkt",
      created: "Element aangemaakt",
      deleted: "Element verwijderd |||| %{smart_count} elementen verwijderd",
      bad_item: "Ongeldig element",
      item_doesnt_exist: "Element bestaat niet",
      http_error: "Fout in de communicatie met de server",
      data_provider_error:
        "Fout in de dataProvider. Bekijk de console voor details.",
      i18n_error:
        "De vertalingen voor de gekozen taal konden niet worden geladen",
      canceled: "Actie geannuleerd",
      logged_out: "Je sessie is verlopen, log opnieuw in.",
      not_authorized: "Je hebt geen toegang tot deze gegevens.",
      application_update_available: "Er is een nieuwe versie beschikbaar.",
      offline: "Geen verbinding. Gegevens konden niet worden opgehaald.",
    },
    validation: {
      required: "Verplicht",
      minLength: "Moet minimaal %{min} tekens bevatten",
      maxLength: "Mag maximaal %{max} tekens bevatten",
      minValue: "Moet minimaal %{min} zijn",
      maxValue: "Mag maximaal %{max} zijn",
      number: "Moet een getal zijn",
      email: "Ongeldig e-mailadres",
      oneOf: "Moet een van de volgende zijn: %{options}",
      regex: "Moet voldoen aan een bepaald formaat (regexp): %{pattern}",
      unique: "Moet uniek zijn",
    },
    saved_queries: {
      label: "Opgeslagen zoekopdrachten",
      query_name: "Naam van de zoekopdracht",
      new_label: "Huidige zoekopdracht opslaan...",
      new_dialog_title: "Huidige zoekopdracht opslaan als",
      remove_label: "Opgeslagen zoekopdracht verwijderen",
      remove_label_with_name: 'Zoekopdracht "%{name}" verwijderen',
      remove_dialog_title: "Opgeslagen zoekopdracht verwijderen?",
      remove_message:
        "Weet je zeker dat je dit item uit je opgeslagen zoekopdrachten wilt verwijderen?",
      help: "Filter de lijst en sla deze zoekopdracht op voor later",
    },
    guesser: {
      empty: {
        title: "Geen gegevens om te tonen",
        message: "Controleer je dataProvider",
      },
    },
    configurable: {
      customize: "Aanpassen",
      configureMode: "Deze pagina configureren",
      inspector: {
        title: "Inspector",
        content:
          "Beweeg over de onderdelen van de applicatie om ze te configureren",
        reset: "Instellingen herstellen",
        hideAll: "Alles verbergen",
        showAll: "Alles tonen",
      },
      Datagrid: {
        title: "Tabel",
        unlabeled: "Kolom zonder label #%{column}",
      },
      SimpleForm: {
        title: "Formulier",
        unlabeled: "Veld zonder label #%{input}",
      },
      SimpleList: {
        title: "Lijst",
        primaryText: "Primaire tekst",
        secondaryText: "Secundaire tekst",
        tertiaryText: "Tertiaire tekst",
      },
    },
  },
};

export default raDutchMessages;
